import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const CustomSelect = ({
    options,
    label,
    value,
    onChange,
    multiple = false,
    isNested = false,
    isDisabled = false,
    className = '',
    classNameIn = '',
    isPortal = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const buttonRef = useRef(null);
    const listboxRef = useRef(null);

    // ---- PORTAL: position calculée hors flux ----
    const [portalStyle, setPortalStyle] = useState({
        top: 0, left: 0, width: 0, maxHeight: 256, placement: "bottom"
    });

    const toggleDropdown = () => {
        if (isDisabled) return;
        setIsOpen((prev) => !prev);
    };

const handleOptionSelect = (option) => {
    if (isNested && option.subOptions) {
        setExpandedIndex(expandedIndex === option.id ? null : option.id);
        return;
    }

    onChange(option);

    if (!multiple) {
        requestAnimationFrame(() => setIsOpen(false));
    }
};


    const filterOptions = (opts) => {
        const q = searchQuery.toLowerCase();
        return opts.filter((option) => {
            const matchesOptionLabel = option.label?.toLowerCase().includes(q);
            const matchesSubOptions = option.subOptions?.some((subOption) =>
                subOption.label.toLowerCase().includes(q)
            );
            return matchesOptionLabel || matchesSubOptions;
        });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                listboxRef.current &&
                !listboxRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    // ---- PORTAL: calcule/maj position sur open/resize/scroll ----
    useEffect(() => {
        if (!isPortal || !isOpen || !buttonRef.current) return;

        let rafId = null;

        const updatePosition = () => {
            const btn = buttonRef.current;
            if (!btn) return;

            const rect = btn.getBoundingClientRect();
            const vwH = window.innerHeight;
            const spaceBelow = vwH - rect.bottom;
            const spaceAbove = rect.top;

            const targetMax = 320;
            let placement = "bottom";
            let maxHeight = Math.min(targetMax, Math.max(200, spaceBelow - 12));

            if (spaceBelow < 220 && spaceAbove > spaceBelow) {
                placement = "top";
                maxHeight = Math.min(targetMax, Math.max(200, spaceAbove - 12));
            }

            const top =
                placement === "bottom"
                    ? rect.bottom + 6 // 👈 plus de window.scrollY
                    : rect.top - maxHeight - 6;

            const left = rect.left;      // 👈 plus de window.scrollX
            const width = rect.width;

            setPortalStyle({ top, left, width, maxHeight, placement });
        };

        const scheduleUpdate = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updatePosition);
        };

        scheduleUpdate();

        // écoute scroll/resize (rAF-throttled)
        window.addEventListener("scroll", scheduleUpdate, { passive: true });
        window.addEventListener("resize", scheduleUpdate, { passive: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener("scroll", scheduleUpdate);
            window.removeEventListener("resize", scheduleUpdate);
        };
    }, [isPortal, isOpen]);

    // ------- Dropdown Items (partagé par inline et portal) -------
    const renderItems = () => {
        if (multiple) {
            return options.map((option, index) => (
                <li
                    key={index}
                    role="option"
                    onClick={() => handleOptionSelect(option.Nom)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseLeave={() => setHighlightedIndex(-1)}
                    className={`relative ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} py-2 pl-4 pr-9 ${option.selected
                        ? "bg-orange-100 text-orange-600 font-semibold"
                        : highlightedIndex === index
                            ? "bg-orange-50 text-orange-600"
                            : "bg-white text-gray-900"
                        } ${classNameIn}`}
                >
                    <span className="block truncate">{option.Nom}</span>
                    {option.selected && (
                        <span className="right-0 absolute inset-y-0 flex items-center pr-4 text-orange-600">
                            <i className="fa-solid fa-check"></i>
                        </span>
                    )}
                </li>
            ));
        }

        if (isNested) {
            return filterOptions(options).map((option, index) => (
                <li key={option.id} className="relative">
                    <div
                        role="option"
                        onClick={() => handleOptionSelect(option)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseLeave={() => setHighlightedIndex(-1)}
                        className={`relative ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} flex items-center justify-between py-3 pl-4 pr-8 ${value === option.label
                            ? "bg-orange-100 text-orange-600 font-semibold"
                            : highlightedIndex === index
                                ? "bg-orange-50 text-orange-600"
                                : "text-gray-800"
                            }`}
                    >
                        <span className="block truncate">{option.label}</span>
                        {option.subOptions && (
                            <span className="flex items-center space-x-2">
                                <span className="bg-orange-600 px-2 py-0.5 rounded-full text-white text-xs">
                                    {option.subOptions.filter((s) => s.selected).length}
                                </span>
                                {expandedIndex === option.id ? (
                                    <i className="fa-caret-down fa" />
                                ) : (
                                    <i className="fa-caret-right fa" />
                                )}
                            </span>
                        )}
                    </div>
                    {expandedIndex === option.id && option.subOptions && (
                        <ul className="bg-gray-50 ml-6 border-gray-300 border-l">
                            {option.subOptions.map((subOption) => (
                                <li
                                    key={subOption.id}
                                    role="option"
                                    onClick={() => onChange(subOption.id)}
                                    className={`${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} relative py-2 pl-8 pr-9 text-sm hover:bg-orange-100 ${subOption.selected ? "text-orange-600 font-semibold" : ""
                                        }`}
                                >
                                    {subOption.label}
                                    {subOption.selected && (
                                        <span className="right-4 absolute text-orange-600">
                                            <i className="fa-solid fa-check"></i>
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </li>
            ));
        }

        return options.map((option, index) => (
            <li
                key={option}
                role="option"
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseLeave={() => setHighlightedIndex(-1)}
                className={`relative ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} py-2 pl-4 pr-9 ${value === option
                    ? "bg-orange-100 text-orange-600 font-semibold"
                    : highlightedIndex === index
                        ? "bg-orange-50 text-orange-600"
                        : "text-gray-900"
                    }`}
            >
                {option}
                {value === option && (
                    <span className="right-0 absolute inset-y-0 flex items-center pr-4 text-orange-600">
                        <i className="fa-solid fa-check"></i>
                    </span>
                )}
            </li>
        ));
    };

    // ---- Inline dropdown (comme avant) ----
    const InlineDropdown = (
        <ul
            ref={listboxRef}
            tabIndex="-1"
            role="listbox"
            aria-labelledby={label}
            className="z-10 absolute bg-white shadow-lg mt-2 border rounded-md w-full max-h-64 overflow-auto sm:text-sm"
        >
            {isNested && (
                <li className="top-0 z-20 sticky bg-gray-200 px-4 py-2 border-b">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher..."
                        className="px-2 py-1 border rounded-md w-full text-sm"
                    />
                </li>
            )}
            {renderItems()}
        </ul>
    );

    // ---- Portal dropdown (hors flux) ----
    const PortalDropdown = createPortal(
        <ul
            ref={listboxRef}
            tabIndex="-1"
            role="listbox"
            aria-labelledby={label}
            className="z-[9999] bg-white shadow-lg border rounded-md overflow-auto sm:text-sm"
            style={{
                position: "absolute",
                top: portalStyle.top,
                left: portalStyle.left,
                width: portalStyle.width,
                maxHeight: portalStyle.maxHeight,
            }}
        >
            {isNested && (
                <li className="top-0 z-20 sticky bg-gray-200 px-4 py-2 border-b">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher..."
                        className="px-2 py-1 border rounded-md w-full text-sm"
                    />
                </li>
            )}
            {renderItems()}
        </ul>,
        document.body
    );

    return (
        <div className="space-y-1 w-full">
            {label && (
                <label className="block font-medium text-gray-700 text-sm leading-5">
                    {label}:
                </label>
            )}
            <div className={`relative rounded border-2 border-gray-300 ${isDisabled ? "" : "hover:border-orange-400"} transition-all duration-300 ${className}`}>
                <button
                    ref={buttonRef}
                    onClick={toggleDropdown}
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className="relative py-2 pr-10 pl-3 border w-full sm:text-sm text-left"
                >
                    <span className="block truncate">{value}</span>
                    <span className="right-0 absolute inset-y-0 flex items-center pr-2 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            />
                        </svg>
                    </span>
                </button>

                {/* Dropdown selon mode */}
                {isOpen && (isPortal ? PortalDropdown : InlineDropdown)}
            </div>
        </div>
    );
};

export default CustomSelect;
